import { Bot, Download, Send, User, X, Minus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useDispatch, useSelector } from "react-redux";
import { clearChat, closeChat, setMinimizeChat } from "../../redux/chatSlice";
import { api1, api5 } from "../../utils/axiosInstance";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useQuery } from "@tanstack/react-query";
import { selectAssessmentData } from "../../redux/assessmentSlice";
import Modal from "../../components/modal/Modal";

const ChatUI = () => {
  const userId = useSelector((state) => state.auth.id);
  const { currentJob } = useSelector((state) => state.user);
  const { firstName } = useSelector((state) => state.auth);
  const { chatType, careerGoal } = useSelector((state) => state.chat);
  const { isOpen, isMinimized } = useSelector((state) => state.chat);
  const assessmentData = useSelector(selectAssessmentData);
  const messagesEndRef = useRef(null);
  const chatContentRef = useRef(null);
  const dispatch = useDispatch();

  const [messages, setMessages] = useState([]); // Initialize with an empty array
  const [input, setInput] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [suggestedReplies, setSuggestedReplies] = useState([
    "Tell me more about the automation potential of my current job",
    "Tell me about emerging AI careers in my industry",
    "How can I upskill to future-proof my career?",
    "What AI tools and technologies can I learn to augment my current job?",
  ]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Since we removed the API call, no quiz data is available
  const quizScoreData = null;

  const handleChatBot = async (query) => {
    try {
      const params = new URLSearchParams({
        query: query,
        id: userId,
        name: firstName,
        occupation: currentJob,
        risk_score: assessmentData?.ai_job_impact_score?.toString() || '', // Use Redux assessment data
        target_career: careerGoal || '',
        country: 'USA',
        ai_literacy_quiz: quizScoreData?.proficiencyLevel || '',
        language: 'en',
      });
      
      let response = await api5.post(`/api/chat-career-genie?${params.toString()}`, '', {
        headers: {
          "Accept": "application/json",
        },
      });

      // if (chatType === "general") {
      //   response = await api4.post(`/api/chat-general`, null, {
      //     params: {
      //       id: userId,
      //       query: query,
      //       name: firstName,
      //       occupation: currentJob,
      //       risk_score: assessmentData?.ai_job_impact_score,
      //     },
      //   });
      // } else if (chatType === "career") {
      //   response = await api4.post(`/api/chat-career`, null, {
      //     params: {
      //       id: userId,
      //       query: query,
      //       target_occupation: careerGoal,
      //       user_name: firstName,
      //       current_occupation: currentJob,
      //     },
      //   });
      // } else {
      //   response = await api4.post(`/api/chat-quiz`, null, {
      //     params: {
      //       id: userId,
      //       query: query,
      //       ai_quiz_score: quizScoreData?.totalScore,
      //       user_name: firstName,
      //       level: quizScoreData?.proficiencyLevel,
      //     },
      //   });
      // }

      const botMessage =
        response?.data.response || "Sorry, I didn't understand that.";

      // Update suggested replies if available
      if (response?.data.ai_suggested_replies) {
        try {
          const parsedReplies = JSON.parse(response.data.ai_suggested_replies);
          setSuggestedReplies(parsedReplies);
        } catch (error) {
          console.error("Failed to parse suggested replies:", error);
          setSuggestedReplies([]);
        }
      } else {
        setSuggestedReplies([]);
      }

      // Simulate bot typing effect
      const words = botMessage.split(" ");
      let chunkedMessage = "";

      setTimeout(() => setIsBotTyping(false), 500);

      setMessages((prevMessages) => [
        ...prevMessages,
        { text: "...", sender: "bot", isPartial: true },
      ]);

      words.forEach((word, index) => {
        setTimeout(() => {
          chunkedMessage += (index === 0 ? "" : " ") + word;

          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            const lastIndex = updatedMessages.length - 1;

            if (
              lastIndex >= 0 &&
              updatedMessages[lastIndex].sender === "bot" &&
              updatedMessages[lastIndex].isPartial
            ) {
              updatedMessages[lastIndex] = {
                ...updatedMessages[lastIndex],
                text: chunkedMessage,
                isPartial: index < words.length - 1,
              };
            }

            return updatedMessages;
          });
        }, 50 * index);
      });

      return response.data;
    } catch (error) {
      console.error("Error:", error);
      setIsBotTyping(false);
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: "Oops! Something went wrong. Try again.", sender: "bot" },
      ]);
    }
  };

  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        if (chatType === "career") {
          // Default message for "career" chat type
          setMessages([
            {
              text: `Hi ${firstName}, how can I help you explore a career in ${careerGoal} or assist you in transitioning from your role as a ${currentJob}?`,
              sender: "bot",
            },
          ]);
          return;
        } else if (chatType === "quiz") {
          // Default message for "quiz" chat type
          setMessages([
            {
              text: `Hello ${firstName}, I see that your AI Literacy score is ${quizScoreData?.totalScore}. You are at the ${quizScoreData?.proficiencyLevel} Level. 
  I can provide guidance to help you improve your AI literacy, point you to useful resources, or answer any questions you may have.`,
              sender: "bot",
            },
          ]);
          return;
        }

        // Fetch history only for "general" chat type
        const response = await api1.get(`/api/chat/history/${userId}`);

        if (response.data.length === 0) {
          // No chat history, show default message
          setMessages([
            {
              text: `Hi ${firstName}, I'm the OnTrack Careers Chat Mentor, here to help you navigate and excel in this growing AI-powered economy! 
  I see that you're currently a ${currentJob}, and AI is reshaping roles like yours.${assessmentData?.ai_job_impact_score ? ` Based on industry trends, your role has a ${assessmentData.ai_job_impact_score}% automation risk.` : ''} I can help you with the questions below or any other questions you have.`,
              sender: "bot",
            },
          ]);
          setSuggestedReplies([
            "How will AI impact my current job role?",
            "What skills should I learn to stay relevant in my industry?",
            "Can you suggest AI-powered tools for my profession?",
            "What are the emerging career opportunities in my field?",
          ]);
        } else {
          // Format and set existing chat history
          const formattedMessages = response.data.map((msg) => ({
            text: msg.message,
            sender: msg.sender,
            createdAt: new Date(msg.createdAt),
          }));

          setMessages(
            formattedMessages.sort((a, b) => a.createdAt - b.createdAt)
          );
        }
      } catch (error) {
        console.error("Error fetching chat history:", error);
      }
    };

    if (userId && isOpen) {
      fetchChatHistory();
    }
  }, [userId, isOpen, chatType, assessmentData]);
  // Runs when chatType or assessmentData changes

  const handleSend = async () => {
    if (!input || !input.trim()) return; // Check if input is defined and not empty

    const userMessage = { text: input, sender: "user" };
    setMessages((prev) => [...prev, userMessage]);
    setInput(""); // Clear the input field

    try {
      // Save user message in DB
      await api1.post("/api/chat/save", {
        userId,
        sender: "user",
        message: input, // Use `message` for the API
      });

      setIsBotTyping(true); // Show typing indicator
      const response = await handleChatBot(input); // Call API with the user's input

      // Save bot response in DB
      await api1.post("/api/chat/save", {
        userId,
        sender: "bot",
        message: response?.response || "I didn't understand that.", // Use `message` for the API
      });
    } catch (error) {
      console.error("Chat error:", error);
      setIsBotTyping(false); // Hide typing indicator on error
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: "Oops! Something went wrong. Try again.", sender: "bot" },
      ]);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (!suggestion || !suggestion.trim()) return;
    setInput(suggestion); // Set the suggestion as input
    const userMessage = { text: suggestion, sender: "user" };
    setMessages((prevMessages) => [...prevMessages, userMessage]); // Add the suggestion as a user message
    setInput(""); // Clear the input field

    setIsBotTyping(true); // Show typing indicator
    
    await api1.post("/api/chat/save", {
      userId,
      sender: "user",
      message: suggestion, // Use `message` for the API
    });

    const response = await handleChatBot(suggestion); // Call API with the user's input

    // Save bot response in DB
    await api1.post("/api/chat/save", {
      userId,
      sender: "bot",
      message: response?.response || "I didn't understand that.", // Use `message` for the API
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isBotTyping, isOpen]);

  const handleClose = () => {
    dispatch(closeChat()); // Dispatch close action
  };

  const handleDownloadPDF = async () => {
    if (isDownloading) return; // Prevent multiple clicks
    setIsDownloading(true);

    try {
      const chatElement = chatContentRef.current;
      if (!chatElement) {
        console.error("Chat element not found");
        setIsDownloading(false);
        return;
      }

      // Store original styles
      const originalMaxHeight = chatElement.style.maxHeight;
      const originalOverflow = chatElement.style.overflow;
      const originalPosition = chatElement.style.position;

      // Set styles for capturing
      chatElement.style.maxHeight = "none";
      chatElement.style.overflow = "visible";
      chatElement.style.position = "relative";

      // Wait for the DOM to update
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Create a new div that will be used for capture
      const captureDiv = document.createElement("div");
      captureDiv.style.position = "absolute";
      captureDiv.style.left = "-9999px";
      captureDiv.style.top = "0";
      captureDiv.style.width = chatElement.offsetWidth + "px";
      captureDiv.style.backgroundColor = "#f9fafb"; // Match the chat background
      captureDiv.style.padding = "20px";
      captureDiv.innerHTML = chatElement.innerHTML;
      document.body.appendChild(captureDiv);

      // Force any LazyLoad elements to load
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const title = "Chat Conversation - CareerGenie";

      // Add title
      pdf.setFontSize(18);
      pdf.text(title, pdfWidth / 2, 15, { align: "center" });
      pdf.setFontSize(12);

      // Add date
      const today = new Date();
      pdf.text(
        `Generated on: ${today.toLocaleDateString()}`,
        pdfWidth / 2,
        22,
        { align: "center" }
      );
      pdf.line(20, 25, pdfWidth - 20, 25);

      // Capture and add content
      try {
        const canvas = await html2canvas(captureDiv, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const imgWidth = pdfWidth - 40; // Margins
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 35; // Start below the title

        // Add first page
        pdf.addImage(imgData, "PNG", 20, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight - 40;

        // Add subsequent pages if needed
        while (heightLeft > 0) {
          position = 20; // Reset position for new page
          pdf.addPage();
          pdf.addImage(
            imgData,
            "PNG",
            20,
            position - (imgHeight - heightLeft),
            imgWidth,
            imgHeight
          );
          heightLeft -= pdfHeight - 40;
        }

        // Save the PDF
        pdf.save("chat-conversation.pdf");
      } catch (error) {
        console.error("Error creating PDF:", error);
      }

      // Clean up
      document.body.removeChild(captureDiv);

      // Restore original styles
      chatElement.style.maxHeight = originalMaxHeight;
      chatElement.style.overflow = originalOverflow;
      chatElement.style.position = originalPosition;
    } catch (error) {
      console.error("Error in PDF download process:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleMinimize = () => {
    dispatch(setMinimizeChat(true));
  };

  const handleDelete = async () => {
    setMessages([]); // Clear messages locally
    dispatch(clearChat());

    try {
      await api1.delete(`/api/chat/delete/${userId}`); // Delete chat from the backend
      handleClose();
    } catch (error) {
      console.error("Error clearing chat history:", error);
    } finally {
      setModalOpen(false);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const handleRestore = () => {
    dispatch(setMinimizeChat(false));
  };

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div
        className="fixed bottom-6 right-6 w-[250px] p-2 bg-custom-blue text-white rounded-lg flex justify-between items-center shadow-lg cursor-pointer"
        onClick={handleRestore}
      >
        <h1 className="text-sm font-semibold">Chat Minimized</h1>
        <button onClick={handleRestore} className="text-white">
          <Minus size={18} />
        </button>
      </div>
    );
  }

  return (
    <>
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <div className="p-6 ">
          {/* Modal Title */}
          <h1 className="text-xl font-bold text-gray-900">
            Confirm Chat Deletion
          </h1>

          {/* Warning Message */}
          <p className="text-gray-700 mt-3">
            Are you sure you want to delete this chat history? This action is{" "}
            <span className="font-semibold text-red-600">permanent</span> and
            cannot be undone.
          </p>

          {/* Buttons Container */}
          <div className="flex justify-end mt-6 space-x-3">
            {/* Cancel Button */}
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
            >
              Cancel
            </button>

            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
            >
              Delete Permanently
            </button>
          </div>
        </div>
      </Modal>

      <div className="fixed bottom-6 right-6 w-[90%] sm:w-96 max-h-[80vh] flex flex-col rounded-lg shadow-2xl bg-white z-50">
        <div className="bg-custom-gradient text-white p-4 rounded-t-lg flex justify-between items-center">
          <h1 className="text-lg font-bold">CareerGenie</h1>
          <div className="flex space-x-2">
            <button
              onClick={handleMinimize}
              title="Minimize Chat"
              className="text-white hover:text-gray-300"
            >
              <Minus size={20} />
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="text-white hover:text-gray-300"
              title="Delete Chat"
            >
              <Trash2 size={20} />
            </button>
            <button
              onClick={handleClose}
              title="Close Chat"
              className="text-white hover:text-gray-300"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div
          id="chat-content"
          ref={chatContentRef}
          className="flex-1 p-4 overflow-y-auto bg-gray-50"
        >
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex mb-4 items-start ${
                message.sender === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.sender === "bot" && (
                <div className="flex-shrink-0">
                  <Bot size={24} className="text-gray-600 mr-2" />
                </div>
              )}

              <div
                className={`p-3 rounded-lg max-w-xs md:max-w-md text-sm break-words ${
                  message.sender === "user"
                    ? "bg-custom-blue text-white rounded-tr-none"
                    : "bg-gray-200 text-gray-800 rounded-tl-none"
                }`}
              >
                <ReactMarkdown>{message.text}</ReactMarkdown>
              </div>

              {message.sender === "user" && (
                <div className="flex-shrink-0">
                  <User size={24} className="text-custom-blue ml-2" />
                </div>
              )}
            </div>
          ))}

          {/* Typing Indicator (3 Dots Animation) */}
          {isBotTyping && (
            <div className="flex items-center justify-start mb-4">
              <div className="flex-shrink-0">
                <Bot size={24} className="text-gray-600 mr-2" />
              </div>
              <div className="p-3 rounded-lg max-w-xs bg-gray-200 text-gray-800 text-sm flex space-x-1">
                <span className="dot-animation w-1 h-1 bg-gray-500 rounded-full"></span>
                <span className="dot-animation w-1 h-1 bg-gray-500 rounded-full"></span>
                <span className="dot-animation w-1 h-1 bg-gray-500 rounded-full"></span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
          {/* Suggested Replies */}
          {messages.length === 1 &&
            messages[0].sender === "bot" &&
            suggestedReplies.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {suggestedReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(reply)}
                    className="px-3 py-1 text-xs bg-custom-blue text-white rounded-full shadow-md hover:bg-blue-700 transition-all"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}

          <div className="w-full">
            {/* Flex Row for sm and up */}
            <div className="flex w-full sm:flex-row sm:items-center sm:h-[44px]">
              {/* Input Field */}
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                className="flex-grow border border-gray-300 rounded-l-lg px-4 h-[44px] focus:outline-none text-black"
                placeholder="Type your message..."
              />

              {/* Send Button */}
              <button
                title="Send message"
                onClick={handleSend}
                className="bg-custom-blue text-white px-4 h-[44px] focus:outline-none md:rounded-none sm:rounded-none rounded-r-lg"
              >
                <Send size={20} />
              </button>

              {/* Download Button (icon version for sm+) */}
              <button
                title="Download the Chat"
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                className={`hidden sm:inline-block bg-gray-500 text-white px-4 h-[44px] rounded-r-lg focus:outline-none hover:bg-gray-700 transition ${
                  isDownloading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Download size={20} />
              </button>
            </div>

            {/* Download Chat text version (for small screens only) */}
            <div className="block sm:hidden mt-2">
              <button
                title="Download the Chat"
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                className={`w-full bg-gray-500 text-white px-4 py-2 rounded-lg focus:outline-none hover:bg-gray-700 transition ${
                  isDownloading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Download Chat
              </button>
            </div>
          </div>
        </div>

        {/* Dot Animation Styles */}
        <style>
          {`
          @keyframes blink {
            0% { opacity: 0.2; }
            50% { opacity: 1; }
            100% { opacity: 0.2; }
          }
          .dot-animation:nth-child(1) {
            animation: blink 1.5s infinite 0.2s;
          }
          .dot-animation:nth-child(2) {
            animation: blink 1.5s infinite 0.4s;
          }
          .dot-animation:nth-child(3) {
            animation: blink 1.5s infinite 0.6s;
          }
        `}
        </style>
      </div>
    </>
  );
};

export default ChatUI;
